import { z } from 'zod';
import { CharacterTypeEnum } from './character';
/**
 * The `config.json` written to `s3://nx-embeds-<env>/<tenantSlug>/`
 * by the deploy handler and read by the embed-static bundle on
 * mount.
 *
 * Identical safety posture as `PublicCharacterInfoSchema`:
 *   - No apiKey or upstream URL EVER. Every field below must be safe
 *     to serve to anonymous browsers.
 *   - The deep `assertNoSecretLeak` invariant in
 *     `tests/secret-leak.test.ts` enforces this against
 *     fixture-driven characters with secrets stamped at every path
 *     in SECRET_PATHS.
 *
 * Differences from PublicCharacterInfoSchema:
 *   - Carries `apiBaseUrl` (the API Gateway endpoint the embed page
 *     POSTs chat / voice to). Public by design — the same value is
 *     baked into the admin UI's Vite bundle.
 *   - Carries `tenantSlug` so the embed page can self-identify in
 *     telemetry.
 *   - Carries `deployedAt` (ISO timestamp) so the admin Deploy panel
 *     can show "stale" / "redeploy needed" indicators when the
 *     character record's `updatedAt` is newer.
 *
 * The schema is intentionally flat: the embed bundle is small and
 * mounts with O(1) shape inspection. New optional fields are
 * encouraged; new required fields need a migration plan because
 * existing tenants' config.json may predate them.
 */
export const EmbedConfigSchema = z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    tenantSlug: z.string().min(1),
    name: z.string().min(1),
    type: CharacterTypeEnum,
    /**
     * Optional: full PNGTuber v2 manifest payload (already redacted
     * before serialisation). Present for `type === 'pngtuber'`
     * characters that have a manifestVersion === 2 record.
     */
    pngtuberV2Manifest: z.unknown().nullable(),
    /**
     * Optional: model3d slot — see `PublicCharacterInfoSchema.model3d`
     * for the same shape. Carries `modelPath` (absolute CloudFront
     * URL) and the texture pack / animation bindings the runtime
     * needs.
     */
    model3d: z.unknown().nullable(),
    voice: z.object({
        provider: z.enum(['browser', 'voxcpm', 'voai', 'brainserver']),
        asrEnabled: z.boolean(),
        tts: z.object({
            provider: z.enum(['voxcpm2', 'voai', 'brainserver', 'browser', 'none']),
        }),
    }),
    /** API Gateway base URL the embed page POSTs chat / voice to. Public. */
    apiBaseUrl: z.string().url(),
    /** ISO timestamp when this config was written. Drives stale-detection in admin Deploy panel. */
    deployedAt: z.string().min(1),
    /**
     * Per `embed-floating-widget` capability — customer-side integration
     * config. The loader script fetches this config.json and reads
     * `integration.*` to decide widget mode / icon / position / etc.
     * Always present in config.json (defaults are applied during deploy
     * even when the character record's `embed.integration` is absent).
     */
    integration: z.object({
        mode: z.enum(['floating', 'iframe-only', 'disabled']),
        defaultLaunchMode: z.enum(['text', 'voice']),
        icon: z.union([
            z.object({ kind: z.literal('character-3d') }),
            z.object({ kind: z.literal('custom-image'), url: z.string().url() }),
        ]),
        position: z.enum(['bottom-right', 'bottom-left']),
        accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        useAccentBackground: z.boolean(),
        /** Per-tenant chat bubble + input colors. Each optional;
         *  absent → runtime built-in defaults. */
        colors: z.object({
            aiBubbleBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
            aiBubbleText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
            userBubbleBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
            userBubbleText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
            inputBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
            inputText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        }).optional(),
    }),
    /**
     * Per `character-custom-audio-clips`: minimal clip catalog mirrored
     * from public-info so static embed pages can drive Trigger A/B/C/D
     * playback at mount time without an extra fetch. Optional with
     * empty-array default for config.json bakes that predate this
     * change.
     */
    audioClips: z.array(z.object({
        id: z.string().uuid(),
        url: z.string().url(),
        contentType: z.enum(['audio/mpeg', 'audio/wav', 'audio/ogg']),
        durationSec: z.number().positive().optional(),
    })).default([]),
    greeting: z.object({
        enabled: z.boolean(),
        text: z.string(),
        clipId: z.string().uuid().optional(),
    }).optional(),
});
